import { DirectUploadProgressContext } from "./ProgressContext_DirectUpload.svelte"
import { PreviewProcessingProgressContext } from "./ProgressContext_PreviewProcess.svelte"
import { Toast } from "toaster"

/**
 * How often a follower re-asks whether a job has started for its scene. Renders start in the
 * editor, usually in another tab, and their live progress events go only to that tab; the job
 * registry is the one place this page can learn about them. Every follower re-watching at once
 * costs one batched status request per interval for the whole page (ProgressContext coalesces
 * the polls), and the answer for an idle scene is empty and dropped.
 */
export const SCENE_JOB_RECHECK_MS = 30_000

type Outcome = {
	onFail?: (...details: unknown[]) => void
	onSuccess?: () => void | Promise<void>
	onCancelled?: () => void
}

/**
 * Reactive view of what is happening to one scene's GPU jobs: a render, direct-upload processing
 * or a preview. Renders and direct-upload processing share the "project" job kind, so one context
 * covers both. Used by the design tile and the show overview so they show the same thing.
 *
 * Call `follow` from a `$effect` and return its cleanup; `isProcessing`, `progress` and
 * `isQueued` then track the scene for as long as the effect lives.
 */
export class SceneJobFollower {
	isProcessing: boolean = $state(false)
	progress: number | undefined = $state()
	#id: number | undefined = $state()

	/** Waiting for one of the account's GPU slots (or, under Batch, for a container). */
	get isQueued(): boolean {
		const id = this.#id
		if (id === undefined) return false
		return !!DirectUploadProgressContext.queued[id] || !!PreviewProcessingProgressContext.queued[id]
	}

	follow(id: number, opts: { rendered: boolean } & Outcome): () => void {
		this.#id = id
		const callbacks = {
			progress: (prog: number) => {
				this.isProcessing = true
				this.progress = prog
			},
			fail: (...details: unknown[]) => {
				this.isProcessing = false
				opts.onFail?.(...details)
			},
			success: async () => {
				this.isProcessing = false
				try {
					await opts.onSuccess?.()
				} catch (err) {
					// The context calls this without awaiting, so a rejection here would be an
					// unhandled promise rejection and nothing else: the success toast has already
					// fired and isProcessing is already false, so the tile would sit on a stale
					// thumbnail with nothing on screen saying the refresh failed.
					console.error("Could not refresh after processing finished", err)
					Toast.error("Processing finished, but this page could not refresh. Please reload.")
				}
			},
			cancel: () => {
				this.isProcessing = false
				this.progress = undefined
				opts.onCancelled?.()
			},
		}
		const unsubscribePreview = PreviewProcessingProgressContext.subscribe(id, callbacks)
		const unsubscribeUpload = DirectUploadProgressContext.subscribe(id, callbacks)

		// Both contexts, every round. A context drops an id from `watched` as soon as a poll finds
		// no registry record for it, so watching the preview context only once meant a preview job
		// that started LATER was never seen -- and that context binds no queued event either, so
		// the poll is its only source. Re-arming just the upload context left the feature broken
		// for exactly the cross-tab case it exists for.
		const watch = () => {
			DirectUploadProgressContext.watch([id])
			if (!opts.rendered) PreviewProcessingProgressContext.watch([id])
		}
		watch()
		const recheck = setInterval(watch, SCENE_JOB_RECHECK_MS)
		return () => {
			clearInterval(recheck)
			PreviewProcessingProgressContext.unsubscribe(id, unsubscribePreview)
			DirectUploadProgressContext.unsubscribe(id, unsubscribeUpload)
			if (this.#id === id) {
				this.#id = undefined
				this.isProcessing = false
			}
		}
	}
}
